import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/theme/useTheme';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navTextColor = !scrolled ? 'rgba(255,255,255,0.88)' : isDark ? 'rgba(255,255,255,0.82)' : '#091D42';
  const menuLineColor = !scrolled ? '#ffffff' : isDark ? '#ffffff' : '#091D42';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinksRaw = t('home.navbar.links', { returnObjects: true });
  const navLinks = Array.isArray(navLinksRaw)
    ? (navLinksRaw as Array<{ label: string; href: string }>)
    : [];

  const scrollTo = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'backdrop-blur-xl border-b' : 'bg-transparent'
      }`}
      style={scrolled ? {
        background: isDark ? 'rgba(5,13,25,0.95)' : 'rgba(255,255,255,0.88)',
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(9,29,66,0.08)',
      } : undefined}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer">
          <img
            src="https://static.readdy.ai/image/271afe4b942d9424cd7cbb260bed8fcb/70b3d6b49bd42b32ed86fb355f5fdc4b.png"
            alt="SIG MOBI Logo"
            className="h-10 lg:h-12 w-auto object-contain"
          />
        </a>

        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
                className="text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap"
                style={{ color: navTextColor }}
              >
                {link.label}
              </a>
            </li>
          ))}
          <li>
            <a
              href="https://app.sigmobi.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="bg-[#00C4F0] text-[#091D42] text-sm font-semibold px-5 py-2 rounded-full hover:bg-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {t('home.navbar.cta')}
            </a>
          </li>
        </ul>

        <button
          className="md:hidden flex flex-col gap-1.5 w-8 h-8 items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={t('home.navbar.menuLabel')}
        >
          <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} style={{ background: menuLineColor }} />
          <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} style={{ background: menuLineColor }} />
          <span className={`block w-6 h-0.5 transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} style={{ background: menuLineColor }} />
        </button>
      </div>

      {menuOpen && (
        <div
          className="md:hidden backdrop-blur-xl border-t px-6 py-4 flex flex-col gap-4"
          style={{
            background: isDark ? 'rgba(5,13,25,0.98)' : 'rgba(255,255,255,0.96)',
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(9,29,66,0.08)',
          }}
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
              className="text-base font-medium py-2 cursor-pointer"
              style={{ color: isDark ? 'rgba(255,255,255,0.82)' : '#091D42' }}
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://app.sigmobi.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="bg-[#00C4F0] text-[#091D42] text-sm font-semibold px-5 py-3 rounded-full text-center cursor-pointer whitespace-nowrap"
          >
            {t('home.navbar.cta')}
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
